import type { Ctx } from "../../types";
import { Action, invalidate } from "gg-wf-scripts";
import { toast } from "../../toast";
import type { Database } from "@shared/database.types";

type EventEnum = Database["public"]["Enums"]["notification_event"];
type ChannelEnum = Database["public"]["Enums"]["notification_channel"];

const EVENT_VALUES: readonly EventEnum[] = [
  "daily_digest",
  "shift_reminder",
  "job_claimed",
  "job_cancelled",
  "claim_confirmation",
];

const CHANNEL_VALUES: readonly ChannelEnum[] = ["email", "sms"];

function isEvent(v: string | undefined): v is EventEnum {
  return !!v && (EVENT_VALUES as readonly string[]).includes(v);
}

function isChannel(v: string | undefined): v is ChannelEnum {
  return !!v && (CHANNEL_VALUES as readonly string[]).includes(v);
}

function asBool(v: string | boolean | undefined): boolean {
  if (typeof v === "boolean") return v;
  return v === "true" || v === "1" || v === "on";
}

export const set_notification_preference: Action<
  Ctx,
  { event?: string; channel?: string; enabled?: string | boolean }
> = async ({ sb }, data, _params) => {
  if (!isEvent(data.event)) {
    return { ok: false, error: "Invalid notification event" };
  }
  if (!isChannel(data.channel)) {
    return { ok: false, error: "Invalid notification channel" };
  }

  const enabled = asBool(data.enabled);
  const { data: session } = await sb.auth.getSession();
  const userId = session.session?.user?.id;
  if (!userId) return { ok: false, error: "Not signed in" };

  const { error } = await sb
    .from("notification_preferences")
    .update({ enabled })
    .eq("user_id", userId)
    .eq("event", data.event)
    .eq("channel", data.channel);

  if (error) {
    toast({ variant: "error", text: "Couldn't save preference. Try again." });
    return { ok: false, error: error.message };
  }

  invalidate("users");
  toast({
    variant: "success",
    text: enabled ? "Notification enabled." : "Notification disabled.",
  });
  return { ok: true };
};
